import { LeagueMode } from "../../pages/createLeague/modeContent";

export type GwSectionKey = "fixtures" | "playerStats" | "totalGoals" | "standings";

export interface GwSectionSpec {
  key: GwSectionKey;
  required: boolean;
  reasonKey: string;
  feeds: LeagueMode[];
}

export interface GameweekSectionsInput {
  gameweekNumber: number;
  supportedModes: string[];
  sourceType: "FPL" | "MANUAL";
  hasClubs: boolean;
}

/**
 * Pure function replacing the old modal wizard's getWizardSteps — the gameweek
 * operations page renders sections.map(...) with no conditionals of its own.
 * The backend aggregate read (GET /admin/gameweeks/:gwId) returns raw facts;
 * this derives the section set client-side. Implementing the same rule in TS
 * on both sides would create the drift it appears to prevent.
 */
export function gameweekSections(input: GameweekSectionsInput): GwSectionSpec[] {
  const { supportedModes, sourceType, hasClubs } = input;
  const sections: GwSectionSpec[] = [];

  // MANUAL comps have no automated fixture sync — an admin must hand-enter every
  // result before the gameweek can be considered done. FPL comps sync automatically,
  // so this section is present for review/correction but never blocking.
  sections.push({
    key: "fixtures",
    required: sourceType === "MANUAL",
    reasonKey: "admin.gameweekOps.sections.fixtures.reason",
    feeds: supportedModes.includes("SCORE_PREDICTOR") ? ["SCORE_PREDICTOR"] : [],
  });

  const playerStatModes = supportedModes.filter((m): m is LeagueMode => m === "TARGET" || m === "GAMEWEEK_CHALLENGE");
  if (playerStatModes.length > 0) {
    sections.push({
      key: "playerStats",
      required: true,
      reasonKey: "admin.gameweekOps.sections.playerStats.reason",
      feeds: playerStatModes,
    });
  }

  // goalsThisRound feeds the Syndicate tiebreaker (routes/leagues.ts) — needed for
  // review even on an FPL-sourced comp (pre-filled from the proposal) and for manual
  // entry on a non-FPL one.
  if (supportedModes.includes("SYNDICATE")) {
    sections.push({
      key: "totalGoals",
      required: true,
      reasonKey: "admin.gameweekOps.sections.totalGoals.reason",
      feeds: ["SYNDICATE"],
    });
  }

  // Standings need the competition to actually have linked clubs to rank — mirrors
  // the existing guard in GameweeksTab (comp.clubs.length > 0). Shown whenever clubs
  // exist regardless of FINAL_POSITIONS being enabled: it's also how the derived-table
  // prefill for future gameweeks gets seeded.
  if (hasClubs) {
    sections.push({
      key: "standings",
      required: supportedModes.includes("FINAL_POSITIONS"),
      reasonKey: "admin.gameweekOps.sections.standings.reason",
      feeds: supportedModes.includes("FINAL_POSITIONS") ? ["FINAL_POSITIONS"] : [],
    });
  }

  return sections;
}
